import * as React from 'react';
// Import material ui modules
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	UseInputFormResult,
	useInputGoogleMap,
	UseInputGoogleMapResults,
	UseInputGoogleMapState,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	ScrollbarProps,
	FormPopupCoordinatesSelectionProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';

interface UseFormPopupCoordinatesSelectionParameters {
	id: string;
	props: FormPopupCoordinatesSelectionProps,
	showValidations?: boolean;
}

interface UseFormPopupCoordinatesSelectionResults {
	submitForm: () => void;
	googleMapInput: UseInputGoogleMapResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupCoordinatesSelection = (params: UseFormPopupCoordinatesSelectionParameters): UseFormPopupCoordinatesSelectionResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			params.props.onSaveCoordinates({
				latitude: googleMapInput.value.lat ?? 0,
				longitude: googleMapInput.value.lng ?? 0,
			});		
		}
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Address Location',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<UseInputGoogleMapState>(googleMapInput)
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		googleMapInput.changeShowValidationFlag(true);
	}

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for address line 1. 
	 **/
	const generateIdForGoogleMaps = (): string => {
		return `${params.id}-googleMaps`;
	}

	// #endregion private methods


	// #region variables

	// Setup the google map input hook.
	const googleMapInput: UseInputGoogleMapResults = useInputGoogleMap({
		id: generateIdForGoogleMaps(),
		label: '',
		required: true,
		disabled: false,
		currentGpsAccuracy: params.props.location?.gpsAccuracy ?? undefined,
		initialInput: {
			hasMovedLocation: false,
			lat: params.props.location?.latitude ?? 0,
			lng: params.props.location?.longitude ?? 0,
		},
		showValidation: params.showValidations,
	});

	// #endregion private variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidationWithFocus()) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			googleMapInput.focusToInput();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		googleMapInput: googleMapInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupCoordinatesSelectionParameters,
}

// Export results.
export {
	UseFormPopupCoordinatesSelectionResults,
}

export default useFormPopupCoordinatesSelection;